import React from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
 

interface FloatingBackButtonProps {
  fallbackRoute?: string;
}

const FloatingBackButton: React.FC<FloatingBackButtonProps> = ({ 
  fallbackRoute
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  // Smart fallback route determination
  const getSmartFallback = (): string => {
    if (fallbackRoute) return fallbackRoute;
    
    const path = location.pathname;
    
    // Customer routes
    if (path === '/checkout') {
      const vendorId = params.vendorId || new URLSearchParams(location.search).get('vendorId');
      return vendorId ? `/menu/${vendorId}` : '/qr';
    }
    
    if (path.startsWith('/orders/')) {
      return '/orders';
    }
    
    if (path.startsWith('/menu/')) {
      return '/qr';
    }

    if (path.startsWith('/invoices/')) {
      return '/invoices';
    }
    
    // Vendor routes
    if (path.startsWith('/vendor/orders/')) {
      return '/vendor/orders';
    }

    if (path.startsWith('/admin/vendors/')) {
      return '/vendor/orders';
    }

    if (path.startsWith('/vendors/') && path.includes('/invoices')) {
      return '/vendor/orders';
    }

    if (path === '/vendor/qr-codes') {
      return '/vendor/orders';
    }
    
    // Default fallbacks based on path
    if (path.startsWith('/vendor/')) {
      return '/vendor/orders'; // Vendor default
    }
    
    return '/qr'; // Customer default
  };

  const handleBack = () => {
    const currentPath = location.pathname;

    // Determine zone from current path to avoid crossing roles
    const inVendorZone = currentPath.startsWith('/vendor/') || currentPath.startsWith('/admin/') || currentPath.startsWith('/vendors/');
    const inStewardZone = currentPath.startsWith('/steward/');
    const roleKey = inVendorZone ? 'VENDOR' : inStewardZone ? 'STEWARD' : 'CUSTOMER';

    // Never rely on browser history for critical flows
    if (currentPath.startsWith('/menu/')) {
      navigate('/qr');
      return;
    }
    if (currentPath === '/qr') {
      // Route to a safe entry for the same role zone
      const safeHome = roleKey === 'VENDOR' ? '/vendor/orders' : roleKey === 'STEWARD' ? '/steward/dashboard' : '/';
      navigate(safeHome);
      return;
    }

    // Use role-scoped navigation stack for multi-step back behavior
    const stackKey = `navStack:${roleKey}`;
    let stack: string[] = [];
    try {
      stack = JSON.parse(sessionStorage.getItem(stackKey) || '[]');
      if (!Array.isArray(stack)) stack = [];
    } catch {
      stack = [];
    }

    // Remove the current path if it's at the top
    if (stack.length > 0 && stack[stack.length - 1] === currentPath) {
      stack.pop();
    }

    const blockedAuth = new Set(['/login', '/login', '/login', '/signup']);
    const isTransient = (p: string) => p === '/qr';
    const isVendorPath = (p: string) => p.startsWith('/vendor/') || p.startsWith('/admin/') || p.startsWith('/vendors/');
    const isStewardPath = (p: string) => p.startsWith('/steward/');

    // Find the last eligible path in the same zone
    while (stack.length > 0) {
      const candidate = stack.pop() as string;
      const sameZone = (inVendorZone && isVendorPath(candidate)) || (inStewardZone && isStewardPath(candidate)) || (!inVendorZone && !inStewardZone && !isVendorPath(candidate) && !isStewardPath(candidate));
      if (!blockedAuth.has(candidate) && !isTransient(candidate) && sameZone && candidate !== currentPath) {
        // Persist trimmed stack and mark this as a back navigation to avoid push on arrival
        sessionStorage.setItem(stackKey, JSON.stringify(stack));
        sessionStorage.setItem(`navBack:${roleKey}`, JSON.stringify({ from: currentPath, to: candidate }));
        navigate(candidate);
        return;
      }
    }

    // Use smart fallback based on current route and role
    navigate(getSmartFallback());
  };

  // Don't show back button on main routes
  const hideOnRoutes = ['/login', '/signup', '/qr', '/', '/login', '/login'];
  if (hideOnRoutes.includes(location.pathname)) {
    return null;
  }

  return (
    <button
      onClick={handleBack}
      className="fixed flex items-center justify-center focus:outline-none transition-all duration-200 md:hidden"
      style={{
        backgroundColor: 'var(--bg-surface)',
        color: '#2F2F2F', /* basil-todo */
        bottom: '100px',
        left: '10px',
        width: '44px',
        height: '44px',
        border: '1px solid rgba(0, 0, 0, 0.06)',
        borderRadius: '50%',
        cursor: 'pointer',
        zIndex: 1001, // Higher than bottom nav
        // Layered soft shadow for floating effect
        boxShadow:
          '0 2px 6px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.8)',
        transition: 'all 0.2s ease',
        opacity: 1,
        transform: 'scale(1)',
        padding: 0,
        margin: 0,
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.backgroundColor = '#FFFFFF';
        e.currentTarget.style.transform = 'scale(1.03)';
        e.currentTarget.style.boxShadow = '0 3px 8px rgba(0,0,0,0.10), 0 12px 28px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.9)';
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.backgroundColor = '#FFFFFF';
        e.currentTarget.style.transform = 'scale(1)';
        e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.8)';
      }}
      onMouseDown={(e) => {
        e.currentTarget.style.transform = 'scale(0.98)';
      }}
      onMouseUp={(e) => {
        e.currentTarget.style.transform = 'scale(1.02)';
      }}
      aria-label="Go back"
      title="Go back"
    >
      <svg 
        className="transition-all duration-200" 
        fill="none" 
        stroke="currentColor" 
        viewBox="0 0 24 24"
        strokeWidth="2.5"
        style={{
          width: '18px',
          height: '18px',
          // Clean, sharp arrow
          strokeLinecap: 'round',
          strokeLinejoin: 'round',
        }}
      >
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          d="M15 19l-7-7 7-7"
        />
      </svg>
    </button>
  );
};

export default FloatingBackButton;
